import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useMemo } from "react";
import { Wrapper } from "@googlemaps/react-wrapper";
import ModalComponent from "../modalComponent";
import LocationSelectionBar from "../locationSelectionBar";
import "../../../assets/saas/molecules/_location-map-modal.scss";
import { MapContent } from "./mapContent";
import { onOpenMapModal } from "./useMapModal";
import { t } from "i18next";
import { COUNTRY_DEFAULT_LOCALE } from "../../../utils/countryDefaultLocale";
const getLocationKey = (location) => location.locationCode || location.address;
const areBrandsEqual = (left, right) => {
    if (left === right)
        return true;
    if (!left || !right)
        return !left && !right;
    if (left.length !== right.length)
        return false;
    return left.every((brand, index) => brand === right[index]);
};
const areValuesEqual = (left, right) => {
    if (left === right)
        return true;
    if (!left || !right)
        return !left && !right;
    if (left.length !== right.length)
        return false;
    return left.every((value, index) => value === right[index]);
};
const areLocationsEqual = (left, right) => {
    return (left.title === right.title &&
        left.address === right.address &&
        left.lat === right.lat &&
        left.lng === right.lng &&
        left.price === right.price &&
        left.carsCount === right.carsCount &&
        left.schedules === right.schedules &&
        left.locationCode === right.locationCode &&
        left.active === right.active &&
        left.selected === right.selected &&
        left.countLocation === right.countLocation &&
        areBrandsEqual(left.brands, right.brands) &&
        areValuesEqual(left.renterCodes, right.renterCodes));
};
const reconcileLocations = (previous, next) => {
    const previousByKey = new Map(previous.map((location) => [getLocationKey(location), location]));
    const merged = next.map((location) => {
        const prevLocation = previousByKey.get(getLocationKey(location));
        if (!prevLocation)
            return location;
        return areLocationsEqual(prevLocation, location) ? prevLocation : location;
    });
    const sameLength = previous.length === merged.length;
    const sameOrderAndRefs = sameLength && previous.every((location, index) => location === merged[index]);
    return sameOrderAndRefs ? previous : merged;
};
const isReturnLocationCompatible = (pickupLocation, returnLocation) => {
    var _a;
    if (!pickupLocation)
        return true;
    return ((_a = pickupLocation.renterCodes) === null || _a === void 0 ? void 0 : _a.some((renterCode) => { var _a; return (_a = returnLocation.renterCodes) === null || _a === void 0 ? void 0 : _a.includes(renterCode); })) || false;
};
const MapModalLocations = (props) => {
    const [isMapModalShown, setIsMapModalShown] = useState(false);
    const [locationSelected, setLocationSelected] = useState(["", ""]);
    /**
     * watch for open event and get locations data from service, then open the modal and send the data to mapContent through state
     */
    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        window.addEventListener("resize", handleResize);
        const offOpen = onOpenMapModal((payload) => {
            const { locationsCodes = ["", ""] } = payload;
            setLocationSelected(locationsCodes);
            setIsMapModalShown(true);
        });
        return () => {
            offOpen();
            window.removeEventListener("resize", handleResize);
        };
    }, []);
    const { customClass, applyFilters, filters, locationsResults } = props;
    const [activeTab, setActiveTab] = useState("pickup");
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 992);
    const [pickUpLocations, setPickUpLocations] = useState([]);
    const [returnLocations, setReturnLocations] = useState([]);
    const buildLocations = useCallback(() => {
        var _a;
        const { pickUpOfferMapGroup = [], returnOfferMapGroup = [] } = locationsResults || {};
        const [pickupCode, returnCode] = locationSelected;
        const buildLocationData = (availabilitiesOffer, activeCode) => {
            return availabilitiesOffer.map((loc) => {
                const rentersByCode = new Map();
                loc.renters.forEach((renter) => {
                    if (!rentersByCode.has(renter.renterCode)) {
                        rentersByCode.set(renter.renterCode, { renterCode: renter.renterCode, imageMainUrl: renter.imageMainUrl });
                    }
                });
                const renters = Array.from(rentersByCode.values());
                return {
                    title: loc.locationName,
                    address: loc.address,
                    lat: loc.latitude,
                    lng: loc.longitude,
                    price: formatMoney(loc.currencyPrice, loc.currencyCode),
                    carsCount: loc.availability,
                    schedules: loc.time,
                    locationCode: loc.locationCode,
                    brands: renters.map((r) => r.imageMainUrl),
                    active: loc.locationCode === activeCode,
                    renterCodes: renters.map((r) => r.renterCode),
                    countLocation: renters.length,
                };
            });
        };
        const country = ((_a = sessionStorage.getItem("country")) !== null && _a !== void 0 ? _a : "").toUpperCase();
        const formatMoney = (value, currency, locale) => {
            var _a;
            if (locale === void 0) { locale = (_a = COUNTRY_DEFAULT_LOCALE[country]) !== null && _a !== void 0 ? _a : "en-US"; }
            return new Intl.NumberFormat(locale, {
                style: "currency",
                currency,
                currencyDisplay: "narrowSymbol",
                maximumFractionDigits: 0,
            }).format(value);
        };
        return [buildLocationData(pickUpOfferMapGroup, pickupCode), buildLocationData(returnOfferMapGroup, returnCode)];
    }, [locationsResults, locationSelected]);
    useEffect(() => {
        const [nextPickups, nextReturns] = buildLocations();
        setPickUpLocations((previous) => reconcileLocations(previous, nextPickups));
        setReturnLocations((previous) => reconcileLocations(previous, nextReturns));
    }, [buildLocations]);
    const selectedPickUpLocation = useMemo(() => pickUpLocations.find((location) => location.selected), [pickUpLocations]);
    useEffect(() => {
        setReturnLocations((previous) => {
            let changed = false;
            const nextLocations = previous.map((location) => {
                if (!selectedPickUpLocation) {
                    if (!location.active && !location.selected)
                        return location;
                    changed = true;
                    return {
                        ...location,
                        active: false,
                        selected: false,
                    };
                }
                if (isReturnLocationCompatible(selectedPickUpLocation, location))
                    return location;
                if (!location.active && !location.selected)
                    return location;
                changed = true;
                return {
                    ...location,
                    active: false,
                    selected: false,
                };
            });
            return changed ? nextLocations : previous;
        });
    }, [selectedPickUpLocation]);
    const isApply = useMemo(() => {
        const selectPickup = pickUpLocations.find((loc) => loc.selected);
        const selectDropoff = returnLocations.find((loc) => loc.selected);
        return !!selectPickup && !!selectDropoff;
    }, [pickUpLocations, returnLocations]);
    const handleApply = () => {
        var _a, _b, _c, _d;
        if (!isApply)
            return;
        const filterClear = Object.entries(filters).reduce((acc, [key, value]) => {
            acc[key] = Array.isArray(value) ? [] : value;
            return acc;
        }, {});
        applyFilters({
            ...filterClear,
            PickUpLocationCode: ((_b = (_a = pickUpLocations.find((loc) => loc.selected)) === null || _a === void 0 ? void 0 : _a.locationCode) === null || _b === void 0 ? void 0 : _b.trim()) || "",
            ReturnLocationCode: ((_d = (_c = returnLocations.find((loc) => loc.selected)) === null || _c === void 0 ? void 0 : _c.locationCode) === null || _d === void 0 ? void 0 : _d.trim()) || "",
        });
        setIsMapModalShown(false);
    };
    /**
     * When a location is clicked, set it as active and unactive the others, then send the active location to the selection bar through state
     */
    const visibleReturnLocationKeys = useMemo(() => {
        return returnLocations
            .filter((location) => isReturnLocationCompatible(selectedPickUpLocation, location))
            .map(getLocationKey);
    }, [selectedPickUpLocation, returnLocations]);
    return (_jsx(ModalComponent, { isShown: isMapModalShown, hide: () => setIsMapModalShown(false), customClass: customClass, modalContent: _jsx(_Fragment, { children: isDesktop ? (_jsxs("div", { className: "locations-modal-desktop", children: [_jsx(Wrapper, { apiKey: process.env.REACT_APP_GOOGLE_API_KEY || "", children: _jsxs("div", { className: "locations-modal-desktop__container", children: [_jsxs("div", { className: "locations-modal-desktop__map-wrapper", children: [_jsx("div", { className: "locations-modal-desktop__map-label", children: t("pickup") }), _jsx(MapContent, { mapId: "desktop-pickup", locations: pickUpLocations, setLocations: setPickUpLocations })] }), _jsxs("div", { className: "locations-modal-desktop__map-wrapper", children: [_jsx("div", { className: "locations-modal-desktop__map-label", children: t("dropoff") }), _jsx(MapContent, { mapId: "desktop-dropoff", locations: returnLocations, setLocations: setReturnLocations, visibleLocationKeys: visibleReturnLocationKeys, onlyShowMap: pickUpLocations.every((loc) => !loc.selected) }), _jsx("div", { className: "locations-modal-desktop__fixed-modal" })] })] }) }), _jsxs("div", { className: "locations-modal-desktop__footer", children: [isApply && (_jsx("button", { onClick: () => {
                                    setPickUpLocations((previous) => previous.map((loc) => ({ ...loc, active: false, selected: false })));
                                    setReturnLocations((previous) => previous.map((loc) => ({ ...loc, active: false, selected: false })));
                                    setLocationSelected(["", ""]);
                                }, className: "button-outline--black button--rounded", children: t("button-clear") })), !isApply && (_jsx("button", { onClick: () => setIsMapModalShown(false), className: "button-outline--black button--rounded", children: t("cancel-button") })), _jsx("button", { onClick: handleApply, className: "button-primary button--rounded", disabled: !isApply, children: t("button-apply") })] })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { id: "location-tabs", className: "location-map-tabs", children: [_jsx("div", { className: `location-map-tabs__slider ${activeTab === "dropoff" ? "location-map-tabs__slider--right" : ""}` }), _jsx("button", { onClick: () => setActiveTab("pickup"), className: `location-map-tabs__button ${activeTab === "pickup" ? "location-map-tabs__button--active" : ""}`, children: t("pickup") }), _jsx("button", { onClick: () => setActiveTab("dropoff"), className: `location-map-tabs__button ${activeTab === "dropoff" ? "location-map-tabs__button--active" : ""}`, children: t("dropoff") })] }), _jsx(Wrapper, { apiKey: process.env.REACT_APP_GOOGLE_API_KEY || "", children: _jsxs("div", { className: `${pickUpLocations.find((loc) => loc.selected) ? "show-selection-active" : ""} ${activeTab === "pickup" ? "show-mobile-map-pickup" : "show-mobile-map-dropoff"}`, style: { width: "100%", height: "100%", transition: "height 0.3s" }, children: [_jsx(MapContent, { mapId: "mobile-map-pickup", locations: pickUpLocations, setLocations: setPickUpLocations }, "activeTab"), _jsx(MapContent, { mapId: "mobile-map-dropoff", locations: returnLocations, setLocations: setReturnLocations, visibleLocationKeys: visibleReturnLocationKeys }, "activeTab_dropoff")] }) }), _jsx(LocationSelectionBar, { pickupLocation: pickUpLocations.find((loc) => loc.selected) || null, dropoffLocation: returnLocations.find((loc) => loc.selected) || null, onApply: handleApply })] })) }) }));
};
export default MapModalLocations;
